/**
 * previewMode.js
 * Handles PostMessage events from the Admin Control Plane's ThemeBuilder
 * 
 * When the runtime engine is loaded in an iframe with ?preview=true,
 * this service listens for theme injection messages and applies
 * CSS variables to :root dynamically.
 */

let isPreviewMode = false;
let messageHandler = null;

/**
 * Check if we're running in preview mode
 * Preview mode is activated via URL query param: ?preview=true
 */
export function checkPreviewMode() {
    const urlParams = new URLSearchParams(window.location.search);
    isPreviewMode = urlParams.get('preview') === 'true';
    return isPreviewMode;
}

/**
 * Apply CSS variables to :root
 * @param {string} cssString - CSS string with :root { ... } block
 */
function applyCSSVars(cssString) {
    // Remove existing preview style if present
    let styleEl = document.getElementById('preview-theme-vars');
    if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'preview-theme-vars';
        document.head.appendChild(styleEl);
    }
    styleEl.textContent = cssString;
}

/**
 * Handle incoming PostMessage from parent (ThemeBuilder iframe parent)
 * @param {MessageEvent} event - PostMessage event
 */
import { reactive } from 'vue';
import { useTenantStore } from '../store/tenantStore';

export const previewState = reactive({
    islandVariants: {}
});

function handleMessage(event) {
    if (!isPreviewMode) return;

    const { type, css, config, timestamp, islandVariants } = event.data || {};

    if (type === 'THEME_INJECT') {
        console.log('[PreviewMode] Received theme injection:', config, islandVariants);

        // Apply the CSS variables
        if (css) {
            applyCSSVars(css);
        }

        // Apply config values
        if (config) {
            applyConfigVars(config);
            
            // Sync dynamic layout if provided
            if (config.layoutItems) {
                const tenantStore = useTenantStore();
                tenantStore.regions.main = config.layoutItems;
            }
        }

        // Apply island variants
        if (islandVariants) {
            Object.assign(previewState.islandVariants, islandVariants);
        }

        // Send confirmation back to parent
        if (event.source && typeof event.source.postMessage === 'function') {
            event.source.postMessage({
                type: 'THEME_APPLIED',
                timestamp: timestamp,
                success: true
            }, '*');
        }
    }
}

/**
 * Handle clicks in preview mode to notify parent
 */
function handleGlobalClick(event) {
    if (!isPreviewMode) return;

    // Find closest island wrapper
    const islandWrapper = event.target.closest('.island-wrapper');
    if (islandWrapper) {
        // Find component ID - in App.vue we iterate tenantStore.regions.main
        // We might need to add data-island-id to the wrapper in App.vue to make this easier
        // For now, let's try to infer or if not present, just send a generic click
        // Actually, let's update App.vue to add data-island-id

        let islandId = islandWrapper.getAttribute('data-island-id');
        if (!islandId) {
            // Fallback: try to find component name from class or other attributes
            // But adding data-attribute is cleaner. We will do that in App.vue next.
            islandId = 'Unknown';
        }

        event.preventDefault();
        event.stopPropagation();

        if (window.parent) {
            window.parent.postMessage({
                type: 'ISLAND_CLICK',
                islandId: islandId,
                timestamp: Date.now()
            }, '*');
        }
    }
}

/**
 * Apply config values directly as CSS variables
 * @param {Object} config - Theme configuration object
 */
function applyConfigVars(config) {
    const root = document.documentElement;

    // Helper to set vars flattened
    const setVars = (prefix, obj) => {
        if (!obj) return;
        Object.entries(obj).forEach(([key, val]) => {
            const kebab = key.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
            root.style.setProperty(`--${prefix}-${kebab}`, val);
        });
    };

    if (config.page) setVars('page', config.page);
    if (config.section) setVars('section', config.section);
    if (config.island) setVars('island', config.island);

    // Legacy / Flat structure support
    if (config.pageMaxWidth) root.style.setProperty('--page-max-width', config.pageMaxWidth);
    // ... add others if needed, but the main ones are strictly hierarchical now
}

/**
 * Initialize preview mode listener
 * Call this from main.js or App.vue on mount
 */
export function initPreviewMode() {
    if (!checkPreviewMode()) {
        console.log('[PreviewMode] Not in preview mode, skipping initialization');
        return false;
    }

    console.log('[PreviewMode] Initializing preview mode listener...');

    // Remove any existing listener
    if (messageHandler) {
        window.removeEventListener('message', messageHandler);
        window.removeEventListener('click', handleGlobalClick, true); // useCapture to catch it first
    }

    // Add new listener
    messageHandler = handleMessage;
    window.addEventListener('message', handleMessage);
    window.addEventListener('click', handleGlobalClick, true);

    // Notify parent we're ready
    if (window.parent && window.parent !== window) {
        window.parent.postMessage({
            type: 'PREVIEW_READY',
            timestamp: Date.now()
        }, '*');
    }

    return true;
}

/**
 * Cleanup preview mode listener
 * Call this on unmount
 */
export function destroyPreviewMode() {
    if (messageHandler) {
        window.removeEventListener('message', messageHandler);
        window.removeEventListener('click', handleGlobalClick, true);
        messageHandler = null;
    }
    isPreviewMode = false;
}

export default {
    checkPreviewMode,
    initPreviewMode,
    destroyPreviewMode
};
